import { environment } from "../../config/environment";
import { customFetch } from "../../utils/Auth";

const API_BASE_URL = environment.apiUrl;

const makeApiCall = async (endpoint, options = {}) => {
  const response = await customFetch(`${API_BASE_URL}${endpoint}`, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }

  return response.json();
};

// Query failed jobs from server
export const queryFailedJobs = async (selectedDate = null, lastKey = null, limit = 50, resolvedFilter = "All") => {
  try {
    const queryDate = selectedDate || new Date().toISOString().split("T")[0];
    const params = new URLSearchParams({
      date: queryDate,
      limit: limit.toString(),
      resolvedFilter: resolvedFilter
    });

    if (lastKey) {
      params.append('lastKey', lastKey);
    }

    const result = await makeApiCall(`/oncall/failed-jobs?${params}`);
    return {
      items: result.items || [],
      lastKey: result.lastKey,
      hasMore: result.hasMore || false,
      count: result.count || 0
    };
  } catch (error) {
    console.error("Error querying failed jobs:", error);
    throw error;
  }
};

// Retry a single failed job via server
export const retryFailedJob = async (job) => {
  try {
    const result = await makeApiCall("/oncall/retry-job", {
      method: "POST",
      body: JSON.stringify({ job }),
    });

    return result;
  } catch (error) {
    console.error(`Error retrying job ${job.job_id}:`, error);
    return { success: false, jobId: job.job_id, error: error.message };
  }
};

// Mark a single job as resolved via server
export const markJobAsResolved = async (jobId) => {
  try {
    const result = await makeApiCall("/oncall/mark-resolved", {
      method: "POST",
      body: JSON.stringify({ jobIds: [jobId] }),
    });

    if (result.success && result.results.length > 0) {
      return result.results[0];
    }

    return { success: false, jobId, error: "No result returned" };
  } catch (error) {
    console.error(`Error updating job ${jobId.job_id}:`, error);
    return { success: false, jobId, error: error.message };
  }
};

// Mark a single job as deleted via server
export const markJobAsDeleted = async (job) => {
  try {
    const result = await makeApiCall("/oncall/delete-jobs", {
      method: "POST",
      body: JSON.stringify({ jobs: [job] }),
    });

    if (result.success && result.results.successful.length > 0) {
      return result.results.successful[0];
    } else if (result.success && result.results.failed.length > 0) {
      return result.results.failed[0];
    }

    return { success: false, jobId: job.job_id, error: "No result returned" };
  } catch (error) {
    console.error(`Error marking job ${job.job_id} as deleted:`, error);
    return {
      success: false,
      jobId: job.job_id,
      error: error.message,
    };
  }
};

// Mark multiple jobs as resolved via server
export const markJobsAsResolved = async (jobIds) => {
  try {
    const result = await makeApiCall("/oncall/mark-resolved", {
      method: "POST",
      body: JSON.stringify({ jobIds }),
    });

    return result.results || [];
  } catch (error) {
    console.error("Error in batch update:", error);
    // Return failed results for all jobs
    return jobIds.map((jobId) => ({
      success: false,
      jobId,
      error: error.message,
    }));
  }
};

// Delete multiple job records via server
export const deleteJobRecords = async (jobs) => {
  try {
    const result = await makeApiCall("/oncall/delete-jobs", {
      method: "POST",
      body: JSON.stringify({ jobs }),
    });

    return result.results || { successful: [], failed: [] };
  } catch (error) {
    console.error("Error in batch soft deletion:", error);
    // Return failed results for all jobs
    const failed = jobs.map((job) => ({
      success: false,
      jobId: job.job_id,
      error: error.message,
    }));
    return { successful: [], failed };
  }
};

// Retry multiple failed jobs via server
export const retryFailedJobs = async (jobs) => {
  try {
    const result = await makeApiCall("/oncall/retry-jobs", {
      method: "POST",
      body: JSON.stringify({ jobs }),
    });

    return result.results || [];
  } catch (error) {
    console.error("Error retrying jobs:", error);
    // Return failed results for all jobs
    return jobs.map((job) => ({
      success: false,
      jobId: job.job_id,
      error: error.message,
    }));
  }
};

// Process job retries (Lambda invoke + DynamoDB update) via server
export const processJobRetries = async (jobs) => {
  try {
    // Step 1: Invoke Lambda functions
    const lambdaResults = await retryFailedJobs(jobs);

    // Step 2: Get successful Lambda invocations
    const successfulJobs = lambdaResults.filter((result) => result.success);
    const failedJobs = lambdaResults.filter((result) => !result.success);

    // Step 3: Update DynamoDB for successful jobs
    let dbUpdateResults = [];
    if (successfulJobs.length > 0) {
      // Get the full job objects for successful jobs (we need both job_id and failure_timestamp)
      const successfulJobObjects = jobs.filter((job) =>
        successfulJobs.some((result) => result.jobId === job.job_id)
      );
      dbUpdateResults = await markJobsAsResolved(successfulJobObjects);
    }

    // Step 4: Compile final results
    const dbSuccessful = dbUpdateResults.filter((result) => result.success);
    const dbFailed = dbUpdateResults.filter((result) => !result.success);

    return {
      lambda: {
        successful: successfulJobs,
        failed: failedJobs,
      },
      database: {
        successful: dbSuccessful,
        failed: dbFailed,
      },
    };
  } catch (error) {
    console.error("Error processing job retries:", error);
    throw error;
  }
};

// Get available Lambda functions from server
export const getAvailableLambdaFunctions = async () => {
  try {
    console.log("Making API call to /api/oncall/lambda-functions");
    const result = await makeApiCall("/oncall/lambda-functions");
    console.log("API response:", result);
    return result.functions || [];
  } catch (error) {
    console.error("Error getting available Lambda functions:", error);
    console.error("Error status:", error.status);
    console.error("Error message:", error.message);
    throw error;
  }
};

// Get patient data for Lambda invocation auto-population
export const getPatientLambdaData = async (patientId) => {
  try {
    const result = await makeApiCall(`/oncall/patient-lambda-data/${patientId}`);
    return result.data;
  } catch (error) {
    console.error("Error getting patient Lambda data:", error);
    throw error;
  }
};

export const lookupAudioChunks = async ({
  doctorName,
  month,
  day,
  patientId,
}) => {
  try {
    const result = await makeApiCall("/oncall/audio-lookup", {
      method: "POST",
      body: JSON.stringify({
        doctor_name: doctorName,
        month,
        day,
        patient_id: patientId,
      }),
    });

    return {
      data: result.data || [],
      searchPrefix: result.search_prefix || "",
      bucket: result.bucket || "",
    };
  } catch (error) {
    console.error("Error looking up audio chunks:", error);
    throw error;
  }
};

// Generic Lambda invoker via server
export const invokeLambda = async (
  functionKey,
  eventPayload,
  customInvocationType = null
) => {
  try {
    const result = await makeApiCall("/oncall/invoke-lambda", {
      method: "POST",
      body: JSON.stringify({
        functionKey,
        eventPayload,
        customInvocationType,
      }),
    });

    return result.result;
  } catch (error) {
    console.error(`Error invoking Lambda ${functionKey}:`, error);
    throw error;
  }
};

// Query CloudWatch logs by request ID via server
export const queryLogsByRequestId = async (
  requestId,
  functionName,
  searchHours = 24,
  logLimit = 1000
) => {
  try {
    const params = new URLSearchParams({
      requestId,
      functionName,
      searchHours: searchHours.toString(),
      logLimit: logLimit.toString(),
    });

    const result = await makeApiCall(`/oncall/logs?${params}`);
    return result;
  } catch (error) {
    console.error("Error querying logs:", error);
    return { success: false, error: error.message, logs: [] };
  }
};
